import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowRight, Zap, Play, BarChart3, Download, Paintbrush, MapPin, Lock, RefreshCw } from "lucide-react";

// Module-scope data arrays so React doesn't re-allocate them every render.
const HOW_STEPS = [
    { n: 1, t: "Create Your QR", d: "Register with your business details and WhatsApp number. Get a unique QR code instantly." },
    { n: 2, t: "Place & Share", d: "Print on packaging, display in-store, or share digitally. Your QR connects scanners directly to you." },
    { n: 3, t: "Get WhatsApp Leads", d: "When scanned, visitors enter details and connect via WhatsApp with pre-filled messages." },
];

const FEATURES = [
    { Icon: BarChart3, bg: "bg-brand-500/20", fg: "text-brand-400", t: "Lead Analytics", d: "Track scans, conversions, and engagement metrics in real-time." },
    { Icon: Download, bg: "bg-blue-500/20", fg: "text-blue-400", t: "CSV Export", d: "Download all leads with timestamps and details as Excel files." },
    { Icon: Paintbrush, bg: "bg-purple-500/20", fg: "text-purple-400", t: "Custom Branding", d: "Add your logo and brand colors to QR codes and landing pages." },
    { Icon: MapPin, bg: "bg-orange-500/20", fg: "text-orange-400", t: "Location Tracking", d: "Capture scanner location data for geo-targeted marketing." },
    { Icon: Lock, bg: "bg-red-500/20", fg: "text-red-400", t: "Secure & Private", d: "Encrypted data storage with JWT + OAuth authentication." },
    { Icon: RefreshCw, bg: "bg-yellow-500/20", fg: "text-yellow-400", t: "Auto-Sync", d: "Real-time lead sync to your CRM or Google Sheets." },
];

const HERO_STATS = [
    { value: "10K+", label: "QR Codes" },
    { value: "50K+", label: "Leads Generated" },
    { value: "98%", label: "Satisfaction" },
];

export default function Home() {
    return (
        <div data-testid="home-page">
            <HeroSection />
            <HowItWorksSection />
            <FeaturesSection />
            <CtaSection />
        </div>
    );
}

function HeroSection() {
    return (
        <section className="hero-gradient min-h-screen flex items-center pt-40 pb-16 relative overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-30"></div>
            <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center relative z-10">
                <HeroCopy />
                <HeroQrCard />
            </div>
        </section>
    );
}

function HeroCopy() {
    return (
        <div className="slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-500/15 border border-brand-500/30 mb-4">
                <span className="text-base">🎁</span>
                <span className="text-xs font-semibold text-brand-300">
                    Launch Offer · 30 days FREE on Growth · code <span className="text-brand-200 font-mono">CONNEXA03</span>
                </span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-light mb-8">
                <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse"></span>
                <span className="text-xs font-medium text-gray-400">Every Scan Starts a Conversation</span>
            </div>
            <h1 className="font-display font-extrabold text-5xl md:text-7xl leading-tight mb-6">
                Turn Every <br />
                <span className="gradient-text glow-text">Scan</span> Into a<br />
                <span className="text-whats-green">WhatsApp Lead</span>
            </h1>
            <p className="text-lg text-gray-400 mb-8 max-w-lg leading-relaxed">
                Create smart QR codes that instantly connect scanners to your WhatsApp. Perfect for businesses, events, networking, and product packaging.
            </p>
            <HeroCtas />
            <HeroStats />
        </div>
    );
}

function HeroCtas() {
    return (
        <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/register" className="btn-primary px-8 py-4 rounded-2xl text-lg font-semibold text-white flex items-center justify-center gap-3" data-testid="hero-cta-register">
                <Zap className="w-5 h-5" /> Create Free QR
            </Link>
            <a href="#features" className="glass px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white/10 transition-all flex items-center justify-center gap-3">
                <Play className="w-5 h-5" /> See How It Works
            </a>
        </div>
    );
}

function HeroStats() {
    return (
        <div className="mt-12 flex items-center gap-8">
            {HERO_STATS.map((s, i) => (
                <div key={s.label} className="flex items-center gap-8">
                    {i > 0 && <div className="w-px h-10 bg-white/10"></div>}
                    <div>
                        <div className="text-2xl font-bold gradient-text">{s.value}</div>
                        <div className="text-sm text-gray-500">{s.label}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function HeroQrCard() {
    return (
        <div className="relative float-anim">
            <div className="relative z-10 glass rounded-3xl p-8 glow-green">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-whats-green/20 flex items-center justify-center text-whats-green text-lg font-bold">W</div>
                        <div>
                            <div className="font-semibold text-sm">Business QR</div>
                            <div className="text-xs text-gray-500">Active • 24 leads today</div>
                        </div>
                    </div>
                    <div className="px-3 py-1 rounded-full bg-brand-500/20 text-brand-400 text-xs font-semibold">PRO</div>
                </div>
                <div className="bg-white/5 rounded-2xl p-6 mb-4">
                    <div className="mx-auto mb-4 flex items-center justify-center">
                        <div className="qr-box"><QRCodeCanvas value="https://connexa.in/u/demo" size={150} level="H" /></div>
                    </div>
                    <div className="text-center text-xs text-gray-500">Scan to connect on WhatsApp</div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                    <MiniStat value="156" label="Total Scans" />
                    <MiniStat value="89" label="Leads" />
                    <MiniStat value="57%" label="Conversion" />
                </div>
            </div>
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-500/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-whats-green/10 rounded-full blur-3xl"></div>
        </div>
    );
}

function MiniStat({ value, label }) {
    return (
        <div className="bg-white/5 rounded-xl p-3">
            <div className="text-lg font-bold text-brand-400">{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
        </div>
    );
}

function HowItWorksSection() {
    return (
        <section id="features" className="py-24 relative">
            <div className="max-w-7xl mx-auto px-6">
                <SectionHeader eyebrow="HOW IT WORKS" title="Three Simple Steps" sub="From QR creation to lead generation in under 2 minutes" />
                <div className="grid md:grid-cols-3 gap-8">
                    {HOW_STEPS.map((s) => <HowItWorksCard key={s.n} step={s} />)}
                </div>
            </div>
        </section>
    );
}

function HowItWorksCard({ step }) {
    return (
        <div className="glass rounded-3xl p-8 card-hover relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/5 rounded-full -translate-y-16 translate-x-16 group-hover:bg-brand-500/10 transition-all"></div>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center mb-6 text-2xl font-bold">{step.n}</div>
            <h3 className="font-display font-bold text-xl mb-3">{step.t}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{step.d}</p>
        </div>
    );
}

function FeaturesSection() {
    return (
        <section className="py-24 mesh-gradient relative">
            <div className="max-w-7xl mx-auto px-6">
                <SectionHeader eyebrow="FEATURES" title="Everything You Need" />
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {FEATURES.map((f) => <FeatureCard key={f.t} feature={f} />)}
                </div>
            </div>
        </section>
    );
}

function FeatureCard({ feature }) {
    const { Icon, bg, fg, t, d } = feature;
    return (
        <div className="glass rounded-2xl p-6 card-hover">
            <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${fg}`} />
            </div>
            <h3 className="font-semibold text-lg mb-2">{t}</h3>
            <p className="text-sm text-gray-400">{d}</p>
        </div>
    );
}

function CtaSection() {
    return (
        <section className="py-24 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-900/20 to-brand-800/10"></div>
            <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
                <h2 className="font-display font-bold text-4xl md:text-5xl mb-6">Ready to Transform Your Leads?</h2>
                <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
                    Join thousands of businesses using Connexa to capture leads effortlessly.
                </p>
                <Link to="/register" className="btn-primary px-10 py-5 rounded-2xl text-lg font-bold text-white inline-flex items-center gap-3" data-testid="cta-get-started">
                    Get Started Free <ArrowRight className="w-5 h-5" />
                </Link>
            </div>
        </section>
    );
}

function SectionHeader({ eyebrow, title, sub }) {
    return (
        <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full glass-light text-xs font-semibold text-brand-400 mb-4">{eyebrow}</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mb-4">{title}</h2>
            {sub && <p className="text-gray-400 max-w-2xl mx-auto">{sub}</p>}
        </div>
    );
}
